/**
 * §6.2 — WEIGHTED OVERLAP. The one number this module is judged on.
 *
 * *"WOS = Σ (effective weight × overlap %), result 0-100%."*
 * *"Test case (must equal 75.75%): weights 15, 20, 15, 10, 15, 15, 10 with overlaps
 * 90, 80, 85, 100, 60, 50, 70."*
 *
 * *"N/A handling, configurable per initiative: Option A (0%): N/A counts as 0. Option B
 * (renormalize): N/A capabilities are excluded and the remaining weights are scaled to 100%
 * for that unit; show coverage (for example, 'scored on 80% of the model')."*
 *
 * D-004 confirms it survives the scale change untouched: *"Weighted overlap (§6.2) was always
 * a percentage, so the worked example still has to return exactly 75.75%."*
 *
 * ── THE SHAPE IS NOT MINE TO CHOOSE ────────────────────────────────────────────────────
 *
 * `test/erp-consolidation/weighted-overlap.ts` was written before this package existed and
 * states the contract in its header. It probes for `@platform/erp-scoring`, binds to
 * `weightedOverlap` with no edit, and runs the same eight cases through this implementation
 * and A8's specification demanding EXACT agreement. The signature below is that contract:
 * rows in integer tenths, `naOption`, a result carrying `pctTenThousandths`.
 *
 * The three fields beyond `pctTenThousandths` — `numerator`, `denominator`, `exact` — are
 * A8's, kept because that test's headline section reads `exact` and `coverageTenths` off the
 * result and because they are the only way a reader can tell whether any rounding happened.
 *
 * ── WHAT THE WEIGHTS ARE, WHICH IS THE THING MOST EASILY GOT WRONG ─────────────────────
 *
 * An assessment unit is BU × application × FUNCTIONAL AREA (`0096`), so the weights that must
 * balance within one unit are **the children of that area** — which is exactly why §6.2's
 * worked example has seven weights summing to 100 and not a whole-model tree. An area's own
 * weight is what §6.6 rolls up ACROSS areas with; it does not enter one unit's overlap.
 * `rescaleToFullWeight` in `weights.ts` is the conversion for a caller holding whole-model
 * effective weights.
 */
import {
  assertInteger,
  ONE_HUNDRED_PCT_TENTHS,
  roundHalfUp,
  TENTHS_PRODUCT_SCALE,
} from './units';

/** `scoring_config.na_option` — the two options §6.2 names, and no third. */
export type NaOption = 'zero' | 'renormalize';

export interface OverlapRow {
  readonly weightTenths: number;
  readonly overlapTenths: number;
  readonly notApplicable: boolean;
}

export interface WeightedOverlapResult {
  /** The answer, in ten-thousandths of a percentage point. `757500` = 75.75%. */
  readonly pctTenThousandths: number;
  /** Σ(weightTenths × overlapTenths) over the rows that counted. Exact, never rounded. */
  readonly numerator: number;
  /** What the numerator was divided by. `10000` under 'zero'; `scoredWeightTenths × 10` under 'renormalize'. */
  readonly denominator: number;
  /** True when the division came out exact, so a reader knows whether rounding was applied. */
  readonly exact: boolean;
  /**
   * §6.2 option B's *"show coverage (for example, 'scored on 80% of the model')"*, in tenths.
   * `1000` means the whole model was scored.
   */
  readonly coverageTenths: number;
}

/**
 * §6.2, to the digit.
 *
 * Under `'zero'` the denominator is the constant `10000` (see `units.ts`'s derivation) and the
 * result is an EXACT integer with no division performed at all. Under `'renormalize'` the
 * scored weights are scaled to 100% for this unit, which is a real division and the one place
 * rounding enters: half-up, at ten-thousandths.
 *
 * `scoredWeightTenths === 0` under `'renormalize'` means every capability is N/A. There is
 * nothing to renormalize onto, so the answer is **0% on 0% of the model** — not `NaN`, not
 * `Infinity`, and not a throw: a unit where nothing applies is a legal state of the grid
 * (`capability_assessment.is_not_applicable`), and it has to render.
 */
export function weightedOverlap(
  rows: readonly OverlapRow[],
  naOption: NaOption = 'zero',
): WeightedOverlapResult {
  let numerator = 0;
  let scoredWeightTenths = 0;
  let totalWeightTenths = 0;

  for (const r of rows) {
    assertInteger(r.weightTenths, 'weightedOverlap: weightTenths');
    assertInteger(r.overlapTenths, 'weightedOverlap: overlapTenths');
    totalWeightTenths += r.weightTenths;
    if (r.notApplicable) {
      /*
       * Option A (zero): *"N/A counts as 0."* The weight STAYS in the denominator, which is
       * the whole difference between the two options — a unit with half its model N/A scores
       * at most 50% under A and can still score 100% under B.
       */
      if (naOption === 'zero') scoredWeightTenths += r.weightTenths;
      continue;
    }
    scoredWeightTenths += r.weightTenths;
    numerator += r.weightTenths * r.overlapTenths;
  }

  let pctTenThousandths: number;
  let denominator: number;
  let exact: boolean;

  if (naOption === 'zero') {
    denominator = TENTHS_PRODUCT_SCALE;
    pctTenThousandths = numerator;
    exact = true;
  } else {
    if (scoredWeightTenths === 0) {
      return { pctTenThousandths: 0, numerator: 0, denominator: 0, exact: true, coverageTenths: 0 };
    }
    denominator = scoredWeightTenths * 10;
    const scaledNumerator = numerator * 1000;
    pctTenThousandths = roundHalfUp(scaledNumerator, scoredWeightTenths);
    exact = scaledNumerator % scoredWeightTenths === 0;
  }

  return {
    pctTenThousandths,
    numerator,
    denominator,
    exact,
    coverageTenths:
      naOption === 'renormalize'
        ? totalWeightTenths === 0
          ? 0
          : roundHalfUp(scoredWeightTenths * ONE_HUNDRED_PCT_TENTHS, totalWeightTenths)
        : ONE_HUNDRED_PCT_TENTHS,
  };
}

/**
 * §6.2's coverage sentence, verbatim in form: *"scored on 80% of the model"*.
 *
 * §6.2 gives it as *"for example"*, so this is the wording the spec itself used and not a
 * paraphrase. It is produced here rather than in the browser for the same reason §6.1's two
 * sentences are (`formatBalanceMessage`): two renderers eventually say two things.
 *
 * `toFixed(0)` where the coverage is a whole percent, one decimal otherwise — `1000` tenths
 * reads "100%", `755` reads "75.5%" — because §6.2's own example writes "80%" and not "80.0%".
 */
export function formatCoverage(coverageTenths: number): string {
  assertInteger(coverageTenths, 'formatCoverage: coverageTenths');
  const whole = coverageTenths % 10 === 0;
  const text = whole ? `${coverageTenths / 10}` : (coverageTenths / 10).toFixed(1);
  return `scored on ${text}% of the model`;
}

/**
 * The overlap one cell contributes, with §6.3's override rule applied.
 *
 * `capability_assessment` carries `overlap_pct_tenths` AND `overlap_override_pct_tenths` with
 * a CHECK that the override cannot be set without a reason. The precedence — override wins
 * when present — is the only reading the pair admits, and it lives here so that the grid, the
 * scorer and any report cannot each decide it separately.
 */
export function effectiveOverlapTenths(cell: {
  readonly overlapPctTenths: number | null;
  readonly overlapOverridePctTenths: number | null;
}): number | null {
  if (cell.overlapOverridePctTenths !== null) return cell.overlapOverridePctTenths;
  return cell.overlapPctTenths;
}
